import React from 'react';
import { useState } from 'react';
import {
	Box,
	Button,
	Typography,
	Grid,
	Radio,
	FormControlLabel,
	RadioGroup,
	IconButton,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import getSelectLangStyles from './selectLang.styles';
import { useTranslation } from '../../hooks/useTranslation';
import { LANGUAGE_OPTIONS } from '../../constants/languageOptions';
import LanguagePartLabel from './LanguagePartLabel';
import { useSelector } from 'react-redux';
import { getEntityThemedCtaButtonSx } from '../../utils/ctaContrast.utils';
import Footer from '../Sections/Footer';

const SelectLang = ({ handleCancel, customDetails, embedFooter = false }) => {
	const { t, changeLanguage } = useTranslation();
	const theme = useTheme();
	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const selectLang = getSelectLangStyles(isDark, embedFooter);
	const currentLanguage = useSelector(
		(state) => state.translation.currentLanguage || 'en',
	);
	const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

	const handleProceed = () => {
		changeLanguage(selectedLanguage);
		handleCancel();
	};

	const handleLanguageChange = (event) => {
		setSelectedLanguage(event.target.value);
	};

	const header = (
		<>
			<Grid container justifyContent="space-between" alignItems="center">
				<Grid item>
					<Typography
						id="language-dialog-heading"
						component="h2"
						sx={{ ...selectLang.dialogHeading }}
					>
						{t('header.dialog.langSelector.title')}
					</Typography>
				</Grid>
				<Grid item>
					<IconButton
						onClick={handleCancel}
						aria-label={t('common.buttons.close')}
						sx={{ p: 0 }}
					>
						<CloseIcon sx={{ ...selectLang.closeIcon }} />
					</IconButton>
				</Grid>
			</Grid>
			<Typography sx={{ ...selectLang.dialogSubHeading }}>
				{t('header.dialog.langSelector.subTitle')}
			</Typography>
		</>
	);

	const languageList = (
		<RadioGroup
			aria-labelledby="language-dialog-heading"
			value={selectedLanguage}
			onChange={handleLanguageChange}
		>
			<Grid container spacing={0} sx={{ pb: '0.5rem' }}>
				{LANGUAGE_OPTIONS.map(({ code, label, altPart }) => (
					<Grid item xs={12} md={6} key={code} sx={{ margin: '0.5rem 0 0 0' }}>
						<FormControlLabel
							value={code}
							control={
								<Radio
									sx={{
										...selectLang.radio,
										'&.Mui-checked': {
											color: customDetails?.primaryColor,
										},
									}}
								/>
							}
							label={
								<LanguagePartLabel
									code={code}
									label={label}
									altPart={altPart}
								/>
							}
							sx={{
								...selectLang.selectLang,
								backgroundColor:
									selectedLanguage === code
										? selectLang.selectedRowBg
										: 'transparent',
							}}
						/>
					</Grid>
				))}
			</Grid>
		</RadioGroup>
	);

	const proceedButton = (
		<Box sx={{ ...selectLang.buttonContainer }}>
			<Button
				onClick={handleProceed}
				variant="contained"
				sx={{
					...selectLang.proceedButtonBase,
					...getEntityThemedCtaButtonSx(customDetails, isDark),
				}}
			>
				{t('common.buttons.proceed')}
			</Button>
		</Box>
	);

	if (embedFooter) {
		return (
			<Box sx={selectLang.root}>
				<Box sx={selectLang.scrollArea}>
					{header}
					{languageList}
				</Box>
				<Box sx={selectLang.bottomStack}>
					{proceedButton}
					<Footer customDetails={customDetails} embedded />
				</Box>
			</Box>
		);
	}

	return (
		<Box sx={selectLang.root}>
			<Box sx={{ ...selectLang.boxContainer }}>
				{header}
				{languageList}
			</Box>
			{proceedButton}
		</Box>
	);
};

export default SelectLang;
